import React from "react";
import { getTimeZoneLabel } from "../utils/timeZone";
import { btn } from "../styles/buttonStyles";
import { type } from "../styles/typography";
import { formatActivityDuration } from "../utils/formatActivityDuration";

export interface ActivityLogTableItem {
  id: string;
  whenText?: string;
  dateText?: string;
  timeText?: string;
  durationText?: string;
  runningAtUtc?: string | null;
  actionText: string;
  byName: string;
  byEmail?: string;
  status: string;
  changeLines?: string[];
  errorMessage?: string;
  pipelineRunUrl?: string;
}

interface ActivityLogTableProps {
  title: string;
  description: string;
  items: ActivityLogTableItem[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  emptyText?: string;
  whenLabel?: string;
}

const PAGE_BLOCK_SIZE = 10;

const formatSentenceCase = (text: string) => {
  const normalized = text.trim().toLowerCase()
    .replace(/\bazure devops\b/g, "Azure DevOps")
    .replace(/\bazure resource manager\b/g, "Azure Resource Manager")
    .replace(/\bazure\b/g, "Azure")
    .replace(/\baks\b/g, "AKS")
    .replace(/\bapi\b/g, "API")
    .replace(/\bdb\b/g, "DB")
    .replace(/\bdevops\b/g, "DevOps")
    .replace(/\bedtu\b/g, "eDTU")
    .replace(/\bid\b/g, "ID")
    .replace(/\bsdk\b/g, "SDK")
    .replace(/\bsku\b/g, "SKU")
    .replace(/\bsql\b/g, "SQL")
    .replace(/\butc\b/g, "UTC")
    .replace(/\baest\b/g, "AEST")
    .replace(/\bvcpu(s)?\b/g, (_, suffix) => `vCPU${suffix ?? ""}`)
    .replace(/\bvcore(s)?\b/g, (_, suffix) => `vCore${suffix ?? ""}`)
    .replace(/\bvmss\b/g, "VMSS")
    .replace(/\bzip\b/g, "ZIP");

  return normalized.replace(/(^|[.!?]\s+)([a-z])/g, (_, prefix, firstCharacter) =>
    `${prefix}${firstCharacter.toUpperCase()}`);
};

const formatLabelText = (label: string) =>
  label
    .split("•")
    .map((part) => formatSentenceCase(
      part
        .replace(/eDTU/g, "__edtu__")
        .replace(/([a-z])([A-Z])/g, "$1 $2"),
    ).replace(/__edtu__/g, "eDTU"))
    .join(" • ");

const normalizeStatus = (status: string) => status.trim().toLowerCase();

const isCanceledStatus = (status: string) => {
  const normalized = normalizeStatus(status);
  return normalized === "canceled" || normalized === "cancelled";
};

const getStatusBadgeClasses = (status: string) => {
  const normalized = normalizeStatus(status);

  if (normalized === "succeeded") {
    return "bg-green-100 text-green-700";
  }

  if (normalized === "queued") {
    return "bg-slate-100 text-slate-700";
  }

  if (normalized === "running") {
    return "bg-blue-100 text-blue-700";
  }

  if (isCanceledStatus(status)) {
    return "bg-amber-100 text-amber-700";
  }

  return "bg-red-100 text-red-700";
};

const getStatusLabel = (status: string) =>
  isCanceledStatus(status) ? "Cancelled" : status.charAt(0).toUpperCase() + status.slice(1);

const isRunningStatus = (status: string) => normalizeStatus(status) === "running";

const isTerminalStatus = (status: string) =>
  ["succeeded", "failed"].includes(normalizeStatus(status)) || isCanceledStatus(status);

const getValueClasses = (value: string, isAfter = false) => {
  const normalized = value.trim().toLowerCase();

  if (normalized === "online") {
    return isAfter ? "font-semibold text-green-700" : "text-green-600";
  }

  if (normalized === "offline") {
    return isAfter ? "font-semibold text-red-700" : "text-red-600";
  }

  return isAfter ? "font-semibold text-blue-700" : "text-blue-600";
};

const renderChangeLine = (line: string) => {
  const arrowSeparator = " -> ";
  const colonIndex = line.indexOf(": ");
  const arrowIndex = line.indexOf(arrowSeparator);

  if (arrowIndex === -1) {
    if (colonIndex === -1) {
      return <span>{formatSentenceCase(line)}</span>;
    }

    return (
      <span>
        {formatLabelText(line.slice(0, colonIndex))}: {line.slice(colonIndex + 2)}
      </span>
    );
  }

  const rawLabel = colonIndex >= 0 && colonIndex < arrowIndex ? line.slice(0, colonIndex) : "";
  const label = rawLabel ? `${formatLabelText(rawLabel)}: ` : "";
  const valueStartIndex = rawLabel ? colonIndex + 2 : 0;
  const beforeValue = line.slice(valueStartIndex, arrowIndex);
  const afterValue = line.slice(arrowIndex + arrowSeparator.length);

  return (
    <span>
      {label && <span>{label}</span>}
      <span className={getValueClasses(beforeValue)}>{beforeValue}</span>
      <span className="text-gray-400">{" -> "}</span>
      <span className={getValueClasses(afterValue, true)}>{afterValue}</span>
    </span>
  );
};

const ActivityLogTable = ({
  title,
  description,
  items,
  loading,
  page,
  totalPages,
  totalCount,
  onRefresh,
  onPageChange,
  emptyText = "No activity logs found yet.",
  whenLabel = getTimeZoneLabel(),
}: ActivityLogTableProps) => {
  // Show pages in blocks of 10, the arrows move to the next or previous block
  const blockStart = Math.floor((page - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
  const blockEnd = Math.min(blockStart + PAGE_BLOCK_SIZE - 1, totalPages);
  const pageNumbers = Array.from({ length: blockEnd - blockStart + 1 }, (_, index) => blockStart + index);
  const hasRunningItems = items.some((item) => isRunningStatus(item.status) && item.runningAtUtc);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!hasRunningItems) {
      return;
    }

    const updateNow = () => setNowMs(Date.now());
    updateNow();
    const intervalId = window.setInterval(updateNow, 1000);

    return () => window.clearInterval(intervalId);
  }, [hasRunningItems]);

  return (
    <section className="workspace-card mt-2 w-full overflow-hidden border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-1">{title}</h3>
          <p className="text-[11px] text-slate-500">{description}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className={btn.secondarySmall}
        >
          Refresh
        </button>
      </div>
      <hr className="my-1.5 border-gray-200" />

      {loading ? (
        <p className={type.body}>Loading activity logs...</p>
      ) : items.length === 0 ? (
        <p className={type.body}>{emptyText}</p>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto border border-slate-200 bg-white">
            {/* Keeps columns readable on phones, the wrapper scrolls sideways instead of rows growing tall */}
            <table className={`min-w-[760px] w-full divide-y divide-gray-200 ${type.table}`}>
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="px-2.5 py-1.5 font-semibold">When ({whenLabel})</th>
                  <th className="px-2.5 py-1.5 font-semibold">Action</th>
                  <th className="px-2.5 py-1.5 font-semibold">By</th>
                  <th className="px-2.5 py-1.5 font-semibold">Status</th>
                  <th className="px-2.5 py-1.5 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item) => {
                  const changeLines = item.changeLines ?? [];
                  const durationText = isRunningStatus(item.status) && item.runningAtUtc
                    ? formatActivityDuration(null, item.runningAtUtc, nowMs)
                    : isTerminalStatus(item.status) ? item.durationText : undefined;
                  return (
                  <tr key={item.id} className="align-top">
                    <td className="px-2.5 py-1.5 text-gray-600 whitespace-nowrap">
                      {item.dateText ? (
                        <>
                          <div>{item.dateText}</div>
                          <div>{item.timeText}</div>
                          {durationText && <div className="text-gray-500">{durationText}</div>}
                        </>
                      ) : (
                        item.whenText
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-gray-700 whitespace-nowrap">{item.actionText}</td>
                    <td className="px-2.5 py-1.5 text-gray-700">
                      <div className="font-medium">{item.byName || "Unknown"}</div>
                      {item.byEmail && <div className="text-[10px] text-gray-500">{item.byEmail}</div>}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${getStatusBadgeClasses(item.status)}`}
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 text-[10px] text-gray-600">
                        <div className="space-y-0.5">
                          {changeLines.map((line, idx) => (
                          <p key={`${item.id}-change-${idx}`}>{renderChangeLine(line)}</p>
                        ))}
                        {item.errorMessage && <p className="text-red-600">Error: {item.errorMessage}</p>}
                        {changeLines.length === 0 && !item.errorMessage && (
                          <span className="text-gray-400">-</span>
                        )}
                        {item.pipelineRunUrl && (
                          <p>
                            <a
                              href={item.pipelineRunUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-blue-600 hover:text-blue-700"
                            >
                              Open Pipeline Run
                            </a>
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          <div className={`flex flex-wrap items-center justify-between gap-3 ${type.help}`}>
            <p>
              Showing page {page} of {totalPages} ({totalCount} logs)
            </p>
            <div className="flex flex-wrap items-center gap-1">
              {blockStart > 1 && (
                <button
                  type="button"
                  aria-label="Previous 10 pages"
                  onClick={() => onPageChange(blockStart - PAGE_BLOCK_SIZE)}
                  className="min-w-7 rounded-sm px-2 py-0.5 border transition-colors border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                >
                  &lsaquo;
                </button>
              )}
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  aria-current={pageNumber === page ? "page" : undefined}
                  className={`min-w-7 rounded-sm px-2 py-0.5 border transition-colors ${
                    pageNumber === page
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              {blockEnd < totalPages && (
                <button
                  type="button"
                  aria-label="Next 10 pages"
                  onClick={() => onPageChange(blockEnd + 1)}
                  className="min-w-7 rounded-sm px-2 py-0.5 border transition-colors border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                >
                  &rsaquo;
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ActivityLogTable;
